import { ChangeDetectionStrategy, Component, computed, inject, output, signal } from '@angular/core';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatDateFormats,
  NativeDateAdapter,
} from '@angular/material/core';
import {
  MatCalendarCellClassFunction,
  MatDatepickerInputEvent,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CreateScreeningService } from '@/app/pages/screening-brief-create/services/create-screening/create-screening.service';
import {
  daysBetween,
  formatScreeningDay,
  isPastCalendarDate,
  latestScreeningDate,
  lookbackStart,
  matchingLookbackPreset,
  oneYearBefore,
  ScreeningLookbackPreset,
  timelineOffset,
} from './screening-window.helpers';

/** Day / month / year, e.g. 01/10/2026. NativeDateAdapter reads Intl options, not a pattern string. */
const SCREENING_WINDOW_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: null,
  },
  display: {
    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' },
    monthYearLabel: { month: 'short', year: 'numeric' },
    dateA11yLabel: { day: 'numeric', month: 'long', year: 'numeric' },
    monthYearA11yLabel: { month: 'long', year: 'numeric' },
  },
};

/**
 * Screening window: preset ranges back from today, up to 1 year, plus a custom date.
 * The stored day is the screen-from date. The range always ends today.
 */
@Component({
  selector: 'sbs-screening-window',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatDatepickerModule, MatFormFieldModule, MatInputModule],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: DateAdapter, useClass: NativeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: SCREENING_WINDOW_DATE_FORMATS },
  ],
  templateUrl: './screening-window.html',
  styleUrl: './screening-window.scss',
})
export class ScreeningWindow {
  readonly createScreeningService = inject(CreateScreeningService);
  readonly confirmed = output<Date>();

  readonly presets: { id: ScreeningLookbackPreset; label: string }[] = [
    { id: '30d', label: 'Last 30 days' },
    { id: '3m', label: 'Last 3 months' },
    { id: '6m', label: 'Last 6 months' },
    { id: '12m', label: 'Last 12 months' },
  ];

  /** Latest selectable custom day. Today and any later day stay disabled. */
  readonly latestDate: Date = latestScreeningDate();
  /** Earliest custom day: one year before today. */
  readonly minDate: Date = oneYearBefore(new Date());

  private readonly customChosen = signal(false);

  dateClass: MatCalendarCellClassFunction<Date> = () => '';

  readonly pastDatesOnly = (date: Date | null): boolean => isPastCalendarDate(date);

  readonly fromDate = computed(() => this.createScreeningService.screenContentUntil() ?? null);

  readonly selectedPreset = computed<ScreeningLookbackPreset | 'custom' | null>(() => {
    if (this.customChosen()) {
      return 'custom';
    }
    const from = this.fromDate();
    return from ? matchingLookbackPreset(from, new Date()) : null;
  });

  readonly fromLabel = computed(() => {
    const from = this.fromDate();
    return from ? formatScreeningDay(from) : 'Choose a range';
  });

  readonly toLabel = computed(() => formatScreeningDay(new Date()));

  readonly yearAgoLabel = computed(() => formatScreeningDay(oneYearBefore(new Date())));

  readonly dayCount = computed(() => {
    const from = this.fromDate();
    return from ? daysBetween(from, new Date()) : null;
  });

  readonly fill = computed(() => {
    const from = this.fromDate();
    return from ? timelineOffset(from, new Date()) : { left: 100, width: 0 };
  });

  presetClass(id: ScreeningLookbackPreset | 'custom'): string {
    const selected = this.selectedPreset() === id;
    return selected
      ? 'btn-shout btn-shout-xs btn-shout-primary shrink-0'
      : 'btn-shout btn-shout-xs btn-shout-secondary shrink-0';
  }

  selectPreset(preset: ScreeningLookbackPreset): void {
    this.customChosen.set(false);
    this.createScreeningService.screenContentUntil.set(lookbackStart(preset, new Date()));
  }

  chooseCustom(): void {
    this.customChosen.set(true);
  }

  useRange(): void {
    const from = this.fromDate();
    if (!from) {
      return;
    }
    this.confirmed.emit(new Date(from.getTime()));
  }

  onDateChange(event: MatDatepickerInputEvent<Date>): void {
    const value = event.value;
    if (!value || !isPastCalendarDate(value) || startBeforeWindow(value, this.minDate)) {
      return;
    }
    this.customChosen.set(true);
    this.createScreeningService.screenContentUntil.set(new Date(value.getTime()));
  }
}

function startBeforeWindow(value: Date, minDate: Date): boolean {
  return value.getTime() < minDate.getTime() && !sameCalendarDay(value, minDate);
}

function sameCalendarDay(left: Date, right: Date): boolean {
  return left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate();
}
